import React from 'react';
import { Descriptions, Avatar, Tag, Statistic, Row, Col, Card } from 'antd';
import { UserOutlined, TrophyOutlined } from '@ant-design/icons';
import { User } from '../../types';
import dayjs from 'dayjs';

interface UserDetailProps {
  user: User;
}

const UserDetail: React.FC<UserDetailProps> = ({ user }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'unverified':
        return 'orange';
      case 'suspended':
        return 'orange';
      case 'banned':
        return 'red';
      default:
        return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'moderator':
        return 'purple';
      case 'premium_user':
        return 'gold';
      case 'app_user':
        return 'blue';
      case 'web_user':
        return 'gray';
      case 'user':
      default:
        return 'blue';
    }
  };

  return (
    <div className="user-detail">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Row align="middle" gutter={16}>
              <Col>
                <Avatar 
                  size={64} 
                  src={user.profile?.avatar} 
                  icon={<UserOutlined />}
                />
              </Col>
              <Col flex="auto">
                <h2 style={{ margin: 0 }}>
                  {user.name || user.username || 'Unknown User'}
                </h2>
                <p style={{ margin: '4px 0', color: '#666' }}>
                  {user.email || 'No Email'} • {user.phone || 'No Phone'}
                </p>
                <Tag color={getStatusColor(user.status)}>{user.status.toUpperCase()}</Tag>
                <Tag color={getRoleColor(user.role)}>{user.role.replace('_', ' ').toUpperCase()}</Tag>
                {user.phoneVerified && <Tag color="green">PHONE VERIFIED</Tag>}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Energy"
              value={user.energy}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Level"
              value={user.level || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Coins"
              value={user.coins || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Games Played"
              value={user.stats?.totalGamesPlayed || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="User Information">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Name">{user.name || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Email">{user.email || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{user.phone || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Phone Verified">
                <Tag color={user.phoneVerified ? 'green' : 'orange'}>
                  {user.phoneVerified ? 'VERIFIED' : 'UNVERIFIED'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="User Type">
                <Tag color={user.isAppUser ? 'blue' : 'gray'}>
                  {user.isAppUser ? 'APP USER' : 'WEB USER'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(user.status)}>{user.status.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Province">{user.province || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Ward">{user.ward || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Gender">
                {user.gender ? (
                  <Tag color={user.gender === 'male' ? 'blue' : user.gender === 'female' ? 'pink' : 'gray'}>
                    {user.gender.toUpperCase()}
                  </Tag>
                ) : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Birth Year">{user.birthYear || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Referred By">{user.refBy || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Energy">{user.energy}</Descriptions.Item>
              <Descriptions.Item label="Created At">
                {dayjs(user.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Last Login">
                {user.lastLoginAt 
                  ? dayjs(user.lastLoginAt).format('YYYY-MM-DD HH:mm:ss')
                  : 'Never'
                }
              </Descriptions.Item>
              {user.profile?.country && (
                <Descriptions.Item label="Country">{user.profile.country}</Descriptions.Item>
              )}
              {user.profile?.bio && (
                <Descriptions.Item label="Bio" span={2}>{user.profile.bio}</Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {user.stats && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="Statistics">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Total Score"
                    value={user.stats.totalScore}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Achievements"
                    value={user.stats.achievementsCount}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Streak Days"
                    value={user.stats.streakDays}
                    suffix="days"
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title="Games Played"
                    value={user.stats.totalGamesPlayed}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default UserDetail;
